import React, { useState } from 'react';
import {
  Box,
  Text,
  Flex,
  Grid,
  Avatar,
  Badge,
  Spinner,
  Center,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';

// ─── Dept color map ───────────────────────────────────────────────────────────

const DEPT_COLORS = {
  Development:            { bg: '#EFF6FF', border: '#3B82F6', text: '#1D4ED8', avatar: '#3B82F6' },
  Management:             { bg: '#F5F3FF', border: '#7C3AED', text: '#5B21B6', avatar: '#7C3AED' },
  'work logg':            { bg: '#ECFDF5', border: '#059669', text: '#065F46', avatar: '#059669' },
  'Software development': { bg: '#FFF7ED', border: '#EA580C', text: '#9A3412', avatar: '#EA580C' },
  'Quality assurance':    { bg: '#FFF1F2', border: '#E11D48', text: '#9F1239', avatar: '#E11D48' },
  default:                { bg: '#F9FAFB', border: '#6B7280', text: '#374151', avatar: '#6B7280' },
};

const getDeptColor = (dept) => DEPT_COLORS[dept] || DEPT_COLORS.default;

const ROLE_SCHEME = {
  superadmin: 'red',
  admin: 'purple',
  betauser: 'gray',
};

// ─── Member Card ──────────────────────────────────────────────────────────────

const MemberCard = ({ user }) => {
  const c = getDeptColor(user.department);

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.100"
      borderRadius="14px"
      p={4}
      _hover={{ borderColor: c.border, boxShadow: `0 4px 16px ${c.border}20`, transform: 'translateY(-1px)' }}
      transition="all 0.15s"
      cursor="default"
      position="relative"
      overflow="hidden"
    >
      {/* Top color strip */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        h="3px"
        bg={c.border}
        borderTopRadius="14px"
      />

      {/* Avatar + name */}
      <Flex align="center" gap={3} mt={1}>
        <Avatar
          size="md"
          name={user.name}
          bg={c.avatar}
          color="white"
          fontWeight="600"
          fontSize="14px"
          flexShrink={0}
        />
        <Box overflow="hidden">
          <Text fontSize="14px" fontWeight="600" color="gray.800"  mb='0px'>
            {user.name}
          </Text>
          <Text fontSize="12px" color="gray.400" noOfLines={1} mb='0px'>
            {user.designation || '—'}
          </Text>
        </Box>
      </Flex>

      {/* Dept + role */}
      <Flex mt={3} align="center" justify="space-between" gap={2}>
        <Box
          px={2.5}
          py="3px"
          bg={c.bg}
          border="1px solid"
          borderColor={c.border + '40'}
          borderRadius="full"
          maxW="160px"
        >
          <Text fontSize="11px" fontWeight="600" color={c.text} noOfLines={1}>
            {user.department || 'Unassigned'}
          </Text>
        </Box>
        <Badge
          fontSize="10px"
          colorScheme={ROLE_SCHEME[user.role] || 'gray'}
          variant="subtle"
          borderRadius="full"
          px={2}
          textTransform="capitalize"
          flexShrink={0}
        >
          {user.role}
        </Badge>
      </Flex>

      {/* Reporting manager */}
      {user.reportingAuthorities?.length > 0 && (
        <Flex align="center" gap={1.5} mt={3} pt={3} borderTop="1px solid" borderColor="gray.50">
          <Icon viewBox="0 0 24 24" w="11px" h="11px" color="gray.300" flexShrink={0}>
            <path fill="currentColor" d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
          </Icon>
          <Text fontSize="11px" color="gray.400" noOfLines={1} mb='0px'>
            Reports to{' '}
            <Text as="span" fontWeight="500" color="gray.500">
              {user.reportingAuthorities[0].name}
            </Text>
          </Text>
        </Flex>
      )}
    </Box>
  );
};

// ─── Exec Section ─────────────────────────────────────────────────────────────

const ExecSection = ({ exec, members }) => {
  const c = getDeptColor(exec.department);

  return (
    <Box mb={8}>
      {/* Section header */}
      <Flex align="center" gap={3} mb={4}>
        <Avatar
          size="sm"
          name={exec.name}
          bg={c.avatar}
          color="white"
          fontWeight="700"
          fontSize="12px"
        />
        <Box>
          <Text fontSize="14px" fontWeight="700" color="gray.800" mb='0px' lineHeight="1.2">
            {exec.name}
          </Text>
          <Text fontSize="12px" color="gray.400" mb='0px'>{exec.designation}</Text>
        </Box>
        <Badge
          ml={1}
          fontSize="11px"
          colorScheme="green"
          variant="subtle"
          borderRadius="2xl"
          px={2.5}
          py={2.5}
        >
          {members.length} member{members.length !== 1 ? 's' : ''}
        </Badge>
        <Box flex={1} h="1px" bg="gray.100" />
      </Flex>

      {/* Cards grid */}
      <Grid
        templateColumns="repeat(auto-fill, minmax(220px, 1fr))"
        gap={3}
      >
        {members.map((m) => (
          <MemberCard key={m.id} user={m} />
        ))}
        {members.length === 0 && (
          <Text fontSize="13px" color="gray.300" fontStyle="italic">No members</Text>
        )}
      </Grid>
    </Box>
  );
};

// ─── TeamsList ────────────────────────────────────────────────────────────────

const TeamsList = ({ execGroups, orphans, users, loading }) => {
  const [search, setSearch] = useState('');

  if (loading) {
    return (
      <Center h="40vh" flexDirection="column" gap={4}>
        <Spinner size="lg" color="blue.400" thickness="3px" />
        <Text color="gray.400" fontSize="13px">Loading Teams...</Text>
      </Center>
    );
  }

  const q = search.toLowerCase().trim();

  const filterMembers = (members) =>
    q
      ? members.filter(
          (m) =>
            m.name.toLowerCase().includes(q) ||
            (m.designation || '').toLowerCase().includes(q) ||
            (m.department || '').toLowerCase().includes(q)
        )
      : members;

  return (
    <Box>
      {/* Search bar */}
      <InputGroup mb={5} maxW="320px">
        <InputLeftElement pointerEvents="none" h="100%" display="flex" alignItems="center" justifyContent="center">
          <SearchIcon color="gray.300" w="13px" h="13px" mb='0px' />
        </InputLeftElement>
        <Input
          placeholder="Search Team members..."
          fontSize="13px"
          size="sm"
          borderRadius="8px"
          borderColor="gray.200"
          _focus={{ borderColor: 'blue.300', boxShadow: '0 0 0 1px #93C5FD' }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </InputGroup>

      {/* Scrollable content */}
      <Box
        maxH="calc(100vh - 230px)"
        overflowY="auto"
        pr={2}
        sx={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { bg: 'transparent' },
          '&::-webkit-scrollbar-thumb': { bg: 'gray.200', borderRadius: 'full' },
          '&::-webkit-scrollbar-thumb:hover': { bg: 'gray.300' },
        }}
      >
        {/* Exec sections */}
        {execGroups.map((g) => {
          const filtered = filterMembers(g.members);
          if (q && filtered.length === 0) return null;
          return (
            <ExecSection key={g.exec.id} exec={g.exec} members={filtered} />
          );
        })}

        {/* Unassigned */}
        {(() => {
          const filtered = filterMembers(orphans);
          if (filtered.length === 0) return null;
          return (
            <Box mb={8}>
              <Flex align="center" gap={3} mb={4}>
                <Text fontSize="14px" fontWeight="700" color="gray.500">Unassigned</Text>
                <Box flex={1} h="1px" bg="gray.100" />
              </Flex>
              <Grid templateColumns="repeat(auto-fill, minmax(220px, 1fr))" gap={3}>
                {filtered.map((m) => <MemberCard key={m.id} user={m} />)}
              </Grid>
            </Box>
          );
        })()}

        {/* Empty state */}
        {q && execGroups.every((g) => filterMembers(g.members).length === 0) && filterMembers(orphans).length === 0 && (
          <Center h="200px" flexDirection="column" gap={2}>
            <Text color="gray.400" fontSize="14px">No members found for "{search}"</Text>
            <Text color="gray.300" fontSize="12px">Try a different name or designation</Text>
          </Center>
        )}
      </Box>
    </Box>
  );
};

export default TeamsList;